<script setup lang="ts">
const props = defineProps<{
  deal?: Deal
  displaySymbol?: boolean
  displayCountdown?: boolean
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
}>()

const mediaQuery = useMediaQuery()

function navigateToDeal(event: PointerEvent) {
  if (event.metaKey || event.ctrlKey) {
    window.open(getDealRouteUrl(props.deal) + dealUrlSuffix.value, '_blank')
  } else {
    navigateTo(getDealRoute(props.deal) + dealUrlSuffix.value)
  }
}

const dealUrlSuffix = computed(() => {
  const utmParams = [
    props.utmSource ? `utm_source=${props.utmSource}` : '',
    props.utmMedium ? `utm_medium=${props.utmMedium}` : '',
    props.utmCampaign ? `utm_campaign=${props.utmCampaign}` : '',
  ]
    .filter(Boolean)
    .join('&')

  return utmParams ? `?${utmParams}` : ''
})
</script>

<template>
  <a
    class="os-deal-card-wrapper"
    v-if="deal"
    :href="getDealRouteUrl(deal) + dealUrlSuffix"
    @click.prevent="navigateToDeal"
  >
    <ion-card
      class="os-deal-card"
      :class="{ 'os-deal-card--compact': !mediaQuery.isSmall }"
    >
      <os-row cross-axis-alignment="stretch" gap="none">
        <os-usp-image
          v-if="deal.images && deal.images[0] && deal.images[0].uspTemplate"
          class="os-deal-card__image deal-image-ratio"
          :alt-text="deal.shortName"
          :image-src="convertJpgToWebPIfSupported(deal.images[0].lg)"
          :usp-template="deal.images[0].uspTemplate"
          :style="mediaQuery.isSmall ? 'width: 40%' : 'width: 40%'"
        />
        <img
          v-else-if="deal.images && deal.images[0]"
          class="os-deal-card__image deal-image-ratio"
          :alt="deal.shortName"
          :src="convertJpgToWebPIfSupported(deal.images[0].lg)"
          :style="mediaQuery.isSmall ? 'width: 40%' : 'width: 40%'"
          lazy
        />
        <img
          v-else
          class="os-deal-card__image deal-image-ratio"
          :alt="deal.shortName"
          src="https://images.outspot.be/404"
          :style="mediaQuery.isSmall ? 'width: 40%' : 'width: 40%'"
          lazy
        />

        <os-expanded>
          <os-column
            cross-axis-alignment="stretch"
            gap="narrow"
            class="os-deal-card__details"
          >
            <ion-card-title style="font-size: 1rem;">
              <os-clamp :lines="2">{{ stripTags(deal.shortName) }}</os-clamp>
            </ion-card-title>

            <div
              class="vwo-container"
              :id="'vwo-deal-card-container-' + deal.id"
            ></div>

            <os-expanded />

            <template v-if="mediaQuery.isLarge">
              <os-row gap="narrow" cross-axis-alignment="end">
                <os-column cross-axis-alignment="start" gap="none">
                  <s
                    class="h3 price"
                    v-if="
                      deal.originalPrice &&
                      parseFloat(deal.originalPrice) >
                        parseFloat(deal.discountPrice)
                    "
                    ><os-currency
                      :value="deal.originalPrice"
                      :display-symbol="displaySymbol"
                  /></s>
                  <div class="h1 discount-price">
                    <os-currency
                      :value="deal.discountPrice"
                      :display-symbol="displaySymbol"
                    />
                  </div>
                </os-column>

                <os-expanded />

                <ion-button secondary inert size="small" v-if="deal.percentOff">
                  <b><os-percent :value="deal.percentOff * -1" /></b>
                </ion-button>

                <os-countdown
                  v-if="displayCountdown"
                  class="os-deal-card__countdown"
                  :to="deal.endDate"
                  compact
                />
              </os-row>

              <ion-button size="small">
                <ion-icon slot="end" :icon="ioniconsChevronForward"></ion-icon>
                <b style="line-height: 1.5rem; font-size: 1rem">{{
                  $t('label.order_now')
                }}</b>
              </ion-button>
            </template>

            <template v-else>
              <os-row
                cross-axis-alignment="center"
                main-axis-alignment="start"
                gap="narrow"
              >
                <s
                  class="h3 price"
                  v-if="
                    deal.originalPrice &&
                    parseFloat(deal.originalPrice) >
                      parseFloat(deal.discountPrice)
                  "
                  ><os-currency
                    :value="deal.originalPrice"
                    :display-symbol="displaySymbol"
                /></s>
                <div class="h1 discount-price">
                  &nbsp;<os-currency
                    :value="deal.discountPrice"
                    :display-symbol="displaySymbol"
                  />
                </div>
              </os-row>

              <os-row gap="narrow" cross-axis-alignment="stretch">
                <os-expanded v-if="!displayCountdown"></os-expanded>

                <os-column
                  class="os-deal-card__percent-off"
                  v-if="deal.percentOff"
                >
                  <os-percent :value="deal.percentOff * -1" />
                </os-column>

                <os-countdown
                  v-if="displayCountdown"
                  class="os-deal-card__countdown"
                  style="flex-grow: 1"
                  :to="deal.endDate"
                  compact
                />
              </os-row>

              <ion-button size="small">
                <ion-icon slot="end" :icon="ioniconsChevronForward"></ion-icon>
                <b style="font-size: 1rem">{{ $t('label.order_now') }}</b>
              </ion-button>
            </template>
          </os-column>
        </os-expanded>
      </os-row>
    </ion-card>
  </a>
</template>

<style scoped>
.price {
  font-size: 1rem;
  margin-top: 0.2rem;
}

.discount-price {
  font-size: 1.5rem;
  font-weight: bold;
}

.os-deal-card-wrapper {
  text-decoration: none;
}

.os-deal-card {
  background-color: var(--background--primary);
  cursor: pointer;
}

.os-deal-card--compact {
  margin-left: -1rem;
  margin-right: -1rem;
  border-radius: 0;
}

.os-deal-card > .os-row {
  height: 100%;
}

.os-deal-card__image {
  object-fit: contain;
  background-color: white;
  display: flex;
  flex: 0 0 0%;
  flex-basis: auto;
}

.os-deal-card__details {
  padding: 1rem;
}

.os-deal-card--compact .os-deal-card__details {
  padding: 0.75rem 1rem;
}

.os-deal-card__percent-off {
  background-color: var(--header);
  color: var(--white);
  border-radius: 0.25rem;
  padding: 5px;
  flex-shrink: 1;
  flex-grow: 0;
  font-size: 0.65rem;
  font-weight: bold;
}

.os-deal-card__countdown {
  background-color: var(--background--accent);
  color: var(--white);
  border-radius: 0.25rem;
  padding: 5px;
}

.os-deal-card--compact .os-deal-card__countdown {
  padding: 4px;
  font-size: 0.7rem;
}
</style>
